import React from 'react';

import { useIntl } from 'react-intl';
import { Link, useLocation, useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { allowListMessages } from 'src/features/parkingManagement/utils/allowListMessages';
import payStationsMessages from 'src/features/payStations/utils/messages';
import pointsMessages from 'src/features/points/utils/messages';
import { pricingPlansMessages } from 'src/features/pricingPlans/utils/messages';
import subscriptionMessages from 'src/features/subscriptions/utils/messages';
import { useMediaQuery } from 'src/hooks/use-media-query';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { SectionBlock, SectionBlockSeparator, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { Button } from 'src/registry/ui/Button';
import { cn } from 'src/utils';

const ParkingSidePanel = () => {
    const { formatMessage } = useIntl();
    const { pricePlanId, reservationType } = useParams();
    const { parkingId } = useParams();
    const { pathname } = useLocation();
    const { closePanel } = useSidePanel();
    const isMobile = useMediaQuery('(max-width: 639px)');

    const permissionPricing = useHasPermissions([PERMISSION.PRICING]);
    const permissionReservationsSettings = useHasPermissions([PERMISSION.RESERVATIONS_SETTINGS]);
    const permissionParkingAdmin = useHasPermissions([PERMISSION.PARKING_ADMIN_SETTINGS]);
    const permissionSubscriptions = useHasPermissions([PERMISSION.SUBSCRIPTIONS_SETTINGS]);
    const permissionNotificationSettings = useHasPermissions([PERMISSION.PARKING_NOTIFICATIONS_SETTINGS]);
    const permissionAdditionalServices = useHasPermissions([PERMISSION.ADDITIONAL_SERVICES_MANAGE]);
    const permissionUsersManage = useHasPermissions([PERMISSION.USERS_MANAGE]);
    const permissionDetailsManage = useHasPermissions([PERMISSION.PARKING_DETAILS_MANAGE]);

    const parkingManagementUrl = `/parking-management/${parkingId}`;
    const reservationUrl = `${parkingManagementUrl}/reservations`;

    const SidePanelButton = React.useCallback(
        ({
            urlLink,
            title,
            additionalActivePaths = [],
        }: {
            urlLink: string;
            title: { id: string; defaultMessage: string };
            additionalActivePaths?: string[];
        }) => {
            return (
                <Button
                    asChild
                    className="h-8 justify-start py-4"
                    variant={[urlLink, ...additionalActivePaths].includes(pathname) ? 'activeGray' : 'link'}
                    onClick={() => closePanel('parking-management-side-panel')}
                >
                    <Link to={urlLink} style={{ textDecoration: 'none' }}>
                        {formatMessage(title)}
                    </Link>
                </Button>
            );
        },
        [pathname],
    );

    return (
        <>
            <div className={cn({ 'sticky z-40 dark:bg-gray-900': !isMobile })}>
                <SectionBlock className="mt-4 flex flex-col sm:gap-2.5">
                    <SectionBlockTitle className="text-base">
                        {formatMessage(payStationsMessages.parking)}
                    </SectionBlockTitle>
                    {permissionDetailsManage && (
                        <SidePanelButton urlLink={parkingManagementUrl} title={parkingManagementMessages.zones} />
                    )}
                    {permissionDetailsManage && (
                        <SidePanelButton title={pointsMessages.points} urlLink={`${parkingManagementUrl}/points`} />
                    )}
                    {permissionUsersManage && (
                        <SidePanelButton
                            title={parkingManagementMessages.zoneUserAccess}
                            urlLink={`${parkingManagementUrl}/zone-user-access`}
                        />
                    )}

                    {permissionPricing && (
                        <SidePanelButton
                            title={pricingPlansMessages.pricingPlans}
                            urlLink={`${parkingManagementUrl}/pricing-plans`}
                            additionalActivePaths={[
                                `${parkingManagementUrl}/pricing-plans/${pricePlanId}`,
                                `${parkingManagementUrl}/pricing-plans/points`,
                            ]}
                        />
                    )}

                    {permissionReservationsSettings && (
                        <SidePanelButton
                            title={pricingPlansMessages.reservations}
                            urlLink={`${reservationUrl}/dynamic`}
                            additionalActivePaths={[
                                `${reservationUrl}/dynamic/${reservationType}`,
                                `${reservationUrl}/dynamic/${reservationType}/versions`,
                                `${reservationUrl}/fixed`,
                            ]}
                        />
                    )}

                    {permissionSubscriptions && (
                        <SidePanelButton
                            title={subscriptionMessages.subscriptions}
                            urlLink={`${parkingManagementUrl}/subscriptions`}
                        />
                    )}

                    {permissionNotificationSettings && (
                        <SidePanelButton
                            title={parkingManagementMessages.notifications}
                            urlLink={`${parkingManagementUrl}/notifications`}
                        />
                    )}

                    {permissionDetailsManage && (
                        <SidePanelButton
                            title={parkingManagementMessages.reasonsForGuest}
                            urlLink={`${parkingManagementUrl}/guest-reasons`}
                        />
                    )}

                    <SidePanelButton
                        title={parkingManagementMessages.pointsGroups}
                        urlLink={`${parkingManagementUrl}/points-group`}
                    />

                    <SidePanelButton
                        title={allowListMessages.allowLists}
                        urlLink={`${parkingManagementUrl}/allow-lists`}
                    />

                    {permissionParkingAdmin && (
                        <>
                            <SectionBlockSeparator className="my-2" />
                            <SectionBlockTitle className="text-base">
                                {formatMessage(parkingManagementMessages.adminSettings)}
                            </SectionBlockTitle>

                            <SidePanelButton
                                urlLink={`${parkingManagementUrl}/general-settings`}
                                title={parkingManagementMessages.generalSettings}
                            />

                            <SidePanelButton
                                urlLink={`${parkingManagementUrl}/public-information`}
                                title={parkingManagementMessages.publicInformation}
                            />

                            <SidePanelButton
                                urlLink={`${parkingManagementUrl}/entry-exit-points`}
                                title={parkingManagementMessages.entryExitPoints}
                            />
                            <SidePanelButton
                                urlLink={`${parkingManagementUrl}/points-of-interest`}
                                title={parkingManagementMessages.pointsOfInterest}
                            />
                            <SidePanelButton
                                urlLink={`${parkingManagementUrl}/amenities`}
                                title={parkingManagementMessages.amenities}
                            />

                            <SidePanelButton
                                urlLink={`${parkingManagementUrl}/integrations`}
                                title={parkingManagementMessages.integrations}
                            />

                            <SidePanelButton
                                urlLink={`${parkingManagementUrl}/payment-providers`}
                                title={parkingManagementMessages.paymentProviders}
                            />
                            {permissionAdditionalServices && (
                                <SidePanelButton
                                    urlLink={`${parkingManagementUrl}/additional-services`}
                                    title={parkingManagementMessages.additionalServices}
                                />
                            )}
                        </>
                    )}
                </SectionBlock>
            </div>
        </>
    );
};

export default ParkingSidePanel;
